import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, CreditCard, Cctv, Lightbulb, LogOut, Maximize, Minimize, PlugZap, Volume2 } from 'lucide-react'
import { Link, navigate } from '../../../router.js'
import {
  BandeauFige, BandeauLecture, LIBELLE_GRAVITE, LIBELLE_NIVEAU, Pastille, TON_ALIM, TON_GRAVITE,
  fmtAge, fmtHeure, fmtNombre, fmtRatio, nomSalle, renommerSalles, useVueSonde, type Ton,
} from '../commun.js'
import {
  estFige, estIncidents, estOverview, fmtDuree, incidentsATraiter, incidentsOmbre, inconnus, ordonnerZones,
  texteFenetre, tonCompteur, tonNiveau, tonSalle,
  type CompteurDirect, type IncidentSonde, type ZoneSonde,
} from '../hub/modele.js'
import './live.css'
import { useMenuSonde } from '../menu-sonde.js'
import { useMenuSalle } from '../menu-salle.js'

/**
 * MODE SOIRÉE — /infrastructure/live, plein écran pour la tablette de la
 * régie (paysage et portrait), 21/10/2026.
 *
 * Ce qu'on regarde d'un coup d'œil pendant la soirée : le niveau, l'heure et
 * l'âge de la donnée en gros, les salles en couleur, les compteurs de
 * confiance (TPE, caméras qui enregistrent, ports DMX, son) et SEULEMENT les
 * incidents à traiter. Relecture toutes les 10 s (Sonde relève toutes les
 * 15 s : plus souvent n'apporterait rien). Aucune navigation : un seul
 * bouton « Quitter » vers /infrastructure.
 *
 * La page est portalisée dans `body` : `.lc-route` porte un `transform`, qui
 * ancrerait un `position: fixed` à la page au lieu de l'écran.
 *
 * Honnêteté : l'âge affiché GRANDIT tant que Sonde ne répond pas (âge de la
 * donnée + temps écoulé depuis la dernière lecture réussie) ; tout passe en
 * gris, jamais en rouge « hors ligne ». `null` s'écrit « — » ; un appareil
 * sans donnée récente n'est jamais compté en panne.
 */

const INTERVALLE = 10_000

function useHorloge(ms = 1_000): number {
  const [t, setT] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setT(Date.now()), ms)
    return () => clearInterval(id)
  }, [ms])
  return t
}

/** Garde l'écran de la tablette allumé tant que la page est ouverte (si le navigateur le permet). */
function useEcranAllume(): void {
  useEffect(() => {
    type Verrou = { release: () => Promise<void> }
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<Verrou> } }
    if (!nav.wakeLock) return
    let verrou: Verrou | null = null
    let vivant = true
    const prendre = () => {
      if (document.visibilityState !== 'visible') return
      nav.wakeLock!.request('screen').then((v) => { if (vivant) verrou = v; else void v.release().catch(() => undefined) }).catch(() => undefined)
    }
    prendre()
    document.addEventListener('visibilitychange', prendre)
    return () => {
      vivant = false
      document.removeEventListener('visibilitychange', prendre)
      if (verrou) void verrou.release().catch(() => undefined)
    }
  }, [])
}

export function ModeSoireePage() {
  const ov = useVueSonde('overview', estOverview, INTERVALLE)
  const inc = useVueSonde('incidents?status=open,acknowledged&limit=50', estIncidents, INTERVALLE)
  const maintenant = useHorloge()
  useEcranAllume()
  const menuSalle = useMenuSalle()

  const racine = useRef<HTMLDivElement>(null)
  const [pleinEcran, setPleinEcran] = useState(false)
  const pleinEcranDispo = typeof document !== 'undefined' && !!document.fullscreenEnabled

  // Instant de la dernière lecture réussie : l'âge affiché grandit tant que Sonde se tait.
  const [luA, setLuA] = useState<number | null>(null)
  useEffect(() => { if (ov.lecture?.etat === 'ok') setLuA(Date.now()) }, [ov.lecture])

  useEffect(() => {
    const maj = () => setPleinEcran(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', maj)
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.fullscreenElement) navigate('/infrastructure') }
    window.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('fullscreenchange', maj)
      window.removeEventListener('keydown', echap)
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
    }
  }, [])

  const basculerPleinEcran = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
    else void racine.current?.requestFullscreen?.().catch(() => undefined)
  }, [])

  const o = ov.lecture?.etat === 'ok' ? ov.lecture.data : ov.precedente
  const indispo = !!ov.lecture && ov.lecture.etat !== 'ok'
  const fige = indispo || estFige(o?.collector)
  const tonGlobal: Ton = o ? tonNiveau(o.level, fige) : 'gris'
  const age = o && o.data_age_s !== null ? o.data_age_s + (luA !== null ? Math.max(0, (maintenant - luA) / 1000) : 0) : null
  const { salles, autres } = ordonnerZones(o?.zones ?? [])
  const libelleZone = (code: string | null) => (code ? nomSalle(code, o?.zones.find((z) => z.code === code)?.label ?? null) : null)

  const incData = inc.lecture?.etat === 'ok' ? inc.lecture.data : inc.precedente
  const incEchec = !!inc.lecture && inc.lecture.etat !== 'ok'
  const aTraiter = incidentsATraiter(incData?.items ?? [], incidentsOmbre(o))
  const heure = new Date(maintenant).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })

  const page = (
    <div ref={racine} className={`sv-live sv-live--${tonGlobal}`} role="region" aria-label="Mode soirée">
      <header className="sv-live-barre">
        <div className="sv-live-barre__niveau">
          <span className="sv-live-kicker">Mode soirée{o ? ` · ${o.mode === 'evening' ? 'soirée en cours' : o.mode === 'rest' ? 'repos' : '—'}` : ''}</span>
          <span className="sv-live-niveau">
            <span className={`sv-live-point sv-live-point--${tonGlobal}`} aria-hidden="true" />
            {o ? o.level_label : indispo ? 'Supervision indisponible' : 'Lecture de Sonde…'}
          </span>
        </div>
        <div className="sv-live-horloge" aria-live="off">
          <span className="sv-live-heure">{heure}</span>
          <span className={`sv-live-age ${fige ? 'sv-live-age--fige' : ''}`}>Données {fmtAge(age)}</span>
        </div>
        <div className="sv-live-barre__actions">
          {pleinEcranDispo ? (
            <button type="button" className="lc-btn" onClick={basculerPleinEcran}>
              {pleinEcran ? <Minimize size={16} aria-hidden="true" /> : <Maximize size={16} aria-hidden="true" />}
              {pleinEcran ? 'Réduire' : 'Plein écran'}
            </button>
          ) : null}
          <Link className="lc-btn" href="/infrastructure"><LogOut size={16} aria-hidden="true" /> Quitter</Link>
        </div>
      </header>

      {indispo ? <BandeauLecture lecture={ov.lecture} depuis={o?.generated_at ?? null} /> : null}
      {o && !indispo ? <BandeauFige collector={o.collector} /> : null}
      {o?.mains_outage.active ? (
        <p className="sv-live-coupure" role="alert">
          <PlugZap size={22} aria-hidden="true" />
          <span>
            <strong>Coupure secteur{o.mains_outage.since ? ` depuis ${fmtHeure(o.mains_outage.since)}` : ''}.</strong>
            {o.mains_outage.runtime_min !== null ? ` Autonomie de l’onduleur : ${fmtNombre(Math.round(o.mains_outage.runtime_min))} min.` : ''}
          </span>
        </p>
      ) : null}

      <section className="sv-live-salles" aria-label="Salles">
        {salles.length > 0
          ? salles.map((z) => <SalleLive key={z.code} z={z} fige={fige} />)
          : ['ROOM 1', 'ROOM 2', 'ROOM 3'].map((n) => (
            <div key={n} className="sv-live-salle sv-live-salle--gris">
              <span className="sv-live-salle__nom">{n}</span>
              <span className="sv-live-salle__score">—</span>
            </div>
          ))}
      </section>

      {autres.length > 0 ? (
        <ul className="sv-live-zones" aria-label="Autres zones">
          {autres.map((z) => {
            const t = tonSalle(z, fige)
            return (
              <li key={z.code} className={`sv-live-zone sv-live-zone--${t}`} {...menuSalle.salle({ code: z.code, label: z.label })}>
                <span className={`sv-live-point sv-live-point--${t}`} aria-hidden="true" />
                <span className="sv-live-zone__nom">{nomSalle(z.code, z.label)}</span>
                <span className="sv-live-zone__score">{z.counts.total === 0 ? '—' : z.score ?? '—'}</span>
                {z.incidents_open > 0 ? <span className="sv-live-zone__inc">{z.incidents_open} inc.</span> : null}
              </li>
            )
          })}
        </ul>
      ) : null}

      <section className="sv-live-compteurs" aria-label="Compteurs de confiance">
        <CompteurLive icone={<CreditCard size={20} aria-hidden="true" />} titre="TPE joignables" c={o?.live.payment_terminals ?? null} fige={fige} />
        <CompteurLive icone={<Cctv size={20} aria-hidden="true" />} titre="Caméras qui enregistrent" c={o?.live.cameras_recording ?? null} fige={fige} />
        <CompteurLive icone={<Lightbulb size={20} aria-hidden="true" />} titre="Ports DMX actifs" c={o?.live.dmx_ports_active ?? null} fige={fige} />
        {(o?.live.sound ?? []).length === 0 ? (
          <div className="sv-live-compteur sv-live-compteur--gris">
            <span className="sv-live-compteur__titre"><Volume2 size={20} aria-hidden="true" /> Son (LAeq 15 min)</span>
            <span className="sv-live-compteur__valeur">—</span>
            <span className="sv-live-compteur__note">Aucun sonomètre suivi</span>
          </div>
        ) : o!.live.sound.map((s) => (
          <div key={s.uid} className={`sv-live-compteur ${s.minutes_to_limit !== null && !fige ? 'sv-live-compteur--warn' : ''}`}>
            <span className="sv-live-compteur__titre"><Volume2 size={20} aria-hidden="true" /> {libelleZone(s.zone_code) ?? s.uid}</span>
            <span className="sv-live-compteur__valeur">{s.laeq15_db === null ? '—' : `${fmtNombre(Math.round(s.laeq15_db))} dB`}</span>
            <span className="sv-live-compteur__note">
              {s.minutes_to_limit !== null
                ? `Limite atteinte dans ${fmtNombre(Math.max(0, Math.round(s.minutes_to_limit)))} min`
                : s.limit_a_db !== null ? `limite ${fmtNombre(s.limit_a_db)} dB(A)` : 'limite —'}
            </span>
          </div>
        ))}
      </section>

      <section className="sv-live-incidents" aria-labelledby="sv-live-incidents-titre">
        <h2 className="sv-live-titre" id="sv-live-incidents-titre">
          À traiter{incData ? <span className="sv-live-titre__n">{aTraiter.length}</span> : null}
        </h2>
        {incEchec ? <BandeauLecture lecture={inc.lecture} /> : null}
        {!incData ? (
          <p className="sv-live-vide">{inc.lecture ? '—' : 'Lecture des incidents…'}</p>
        ) : aTraiter.length === 0 ? (
          <p className={`sv-live-vide ${fige || incEchec ? '' : 'sv-live-vide--ok'}`}>
            {fige || incEchec ? null : <CheckCircle2 size={22} aria-hidden="true" />}
            Aucun incident à traiter{fige || incEchec ? ' dans la dernière donnée lue' : ''}.
          </p>
        ) : (
          <ul className="sv-live-liste">
            {aTraiter.slice(0, 8).map((i) => <IncidentLive key={i.id} i={i} fige={fige || incEchec} zone={libelleZone(i.zone_code ?? i.root?.zone_code ?? null)} />)}
            {aTraiter.length > 8 ? <li className="sv-live-liste__plus">… et {aTraiter.length - 8} autre{aTraiter.length - 8 > 1 ? 's' : ''}</li> : null}
          </ul>
        )}
      </section>
    </div>
  )

  return typeof document === 'undefined' ? page : createPortal(page, document.body)
}

function SalleLive({ z, fige }: { z: ZoneSonde; fige: boolean }) {
  const ton = tonSalle(z, fige)
  const fen = texteFenetre(z)
  const vide = z.counts.total === 0
  const nInconnus = inconnus(z.counts)
  const menuSalle = useMenuSalle()
  return (
    <Link href={`/infrastructure/salles/${encodeURIComponent(z.code)}`} className={`sv-live-salle sv-live-salle--${ton}`} {...menuSalle.salle({ code: z.code, label: z.label })}>
      <span className="sv-live-salle__tete">
        <span className="sv-live-salle__nom">{nomSalle(z.code, z.label)}</span>
        {z.incidents_open > 0 ? <span className="sv-live-salle__inc">{z.incidents_open} incident{z.incidents_open > 1 ? 's' : ''}</span> : null}
      </span>
      <span className="sv-live-salle__score">{vide ? '—' : z.score ?? '—'}<span className="sv-live-salle__sur">/100</span></span>
      <span className="sv-live-salle__etats">
        {!vide ? <Pastille ton={tonNiveau(z.level, fige)}>{z.level ? LIBELLE_NIVEAU[z.level] ?? z.level : 'Niveau inconnu'}</Pastille> : null}
        <Pastille ton={fige ? 'gris' : TON_ALIM[z.power.situation] ?? 'gris'}>{z.power.situation === 'unknown' ? 'Alimentation inconnue' : z.power.label_fr}</Pastille>
      </span>
      <span className="sv-live-salle__detail">
        {vide ? 'Aucun appareil au registre' : (
          <>
            {fmtRatio(z.counts.online, z.counts.total - z.counts.off_expected)} en ligne
            {z.counts.offline > 0 ? ` · ${z.counts.offline} hors ligne` : ''}
            {z.counts.degraded > 0 ? ` · ${z.counts.degraded} dégradé${z.counts.degraded > 1 ? 's' : ''}` : ''}
            {z.counts.off_expected > 0 ? ` · ${z.counts.off_expected} éteint${z.counts.off_expected > 1 ? 's' : ''} comme prévu` : ''}
            {nInconnus > 0 ? ` · ${nInconnus} sans donnée` : ''}
          </>
        )}
      </span>
      {fen ? <span className="sv-live-salle__fenetre">{fen.enCours ? fen.plage : `${fen.titre} : ${fen.plage}`}{fen.secours ? ' · planning de secours' : ''}</span> : null}
    </Link>
  )
}

function CompteurLive({ icone, titre, c, fige }: { icone: ReactNode; titre: string; c: CompteurDirect | null; fige: boolean }) {
  const ton = fige ? 'gris' : tonCompteur(c)
  const absent = !c || c.ok === null || c.total === null
  return (
    <div className={`sv-live-compteur sv-live-compteur--${ton}`}>
      <span className="sv-live-compteur__titre">{icone} {titre}</span>
      <span className="sv-live-compteur__valeur">{c ? fmtRatio(c.ok, c.total) : '—'}</span>
      <span className="sv-live-compteur__note">
        {absent ? 'Aucun appareil suivi' : (c!.unobserved ?? 0) > 0 ? `${c!.unobserved} sans donnée · ${fmtAge(c!.data_age_s)}` : fmtAge(c!.data_age_s)}
      </span>
    </div>
  )
}

function IncidentLive({ i, fige, zone }: { i: IncidentSonde; fige: boolean; zone: string | null }) {
  const menu = useMenuSonde()
  return (
    <li>
      <Link className="sv-live-incident" href={`/infrastructure/incidents/${encodeURIComponent(i.id)}`}
            {...menu.incident({ id: i.id, titre: renommerSalles(i.title), status: i.status, acquitte: i.acked_at !== null, sourdine: i.snoozed_until ?? null })}>
        <Pastille ton={fige ? 'gris' : TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
        <span className="sv-live-incident__texte">
          <span className="sv-live-incident__titre">{renommerSalles(i.title)}</span>
          <span className="sv-live-incident__meta">
            {[zone, i.root?.display_name, i.status === 'acknowledged' ? i.status_label : null].filter(Boolean).join(' · ')}
          </span>
        </span>
        <span className="sv-live-incident__depuis">
          <span className="sv-live-mono">{fmtHeure(i.opened_at)}</span>
          <span>{fmtDuree(i.duration_s)}</span>
        </span>
      </Link>
    </li>
  )
}
